import { defaultHeaders } from '@/src/utilities/fetch-utilities';
import { formatNumber } from '@/src/utilities/formatters';
import { createResult } from '@/src/utilities/result';

/**
 * Retrieves an origin value for the google maps directions URL
 *  from the user's location and an empty string if no permissions
 *  were given to the site
 * @returns {Promise<string>}
 */
export function getMapDirectionsOrigin() {
  return new Promise((resolve, _) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        resolve(`${latitude},${longitude}`);
      },
      (_) => {
        resolve('');
      },
    );
  });
}

/**
 * See: https://developers.google.com/maps/documentation/javascript/geocoding?hl=en
 * @param {google} google
 * @param {string} postalcode
 * @returns {Promise<import('src/utilities/result').SuccessResult<google.maps.LatLng> | import('src/utilities/result').FailureResult<any>>}
 */
export function geocodePostalCode(google, postalcode) {
  const request = { address: postalcode };

  /** @type {Promise<google.maps.LatLng>} */
  const promise = new Promise((resolve, reject) => {
    new google.maps.Geocoder().geocode(request, (results, status) => {
      if (status !== 'OK') {
        reject(`Invalid geocode response status: ${status}`);

        return;
      }

      if (
        !results ||
        !results.length ||
        !results[0] ||
        !results[0].geometry ||
        !results[0].geometry.location
      ) {
        reject(`Could not retrieve location coordinates: ${status}`);

        return;
      }

      resolve(results[0].geometry.location);
    });
  });

  return createResult(promise);
}

/**
 * @param {google} google
 * @param {MapState} state
 * @param {google.maps.LatLng} latLng
 */
export function resetMap(google, state, latLng) {
  for (const marker of state.markers) {
    marker.setMap(null);
  }

  state.markers = [];

  if (!state.map) {
    return;
  }

  state.map.setCenter(latLng);

  state.markers.push(
    new google.maps.Marker({
      map: state.map,
      position: latLng,
    }),
  );
}

/**
 * @param {google.maps.LatLng} latLng
 * @param {number} searchRadius
 * @param {string} culture
 * @param {string} miles the translated value for the word 'miles'
 */
export function searchLocations(latLng, searchRadius, culture, miles) {
  return createResult(
    fetch('/api/locator', {
      method: 'POST',
      body: JSON.stringify({
        coordLat: latLng.lat(),
        coordLong: latLng.lng(),
        radiusMiles: searchRadius,
      }),
      headers: defaultHeaders(),
    })
      .then((resp) => resp.json())
      .then((/** @type {{ locations: ServiceLocationResponse[] }} */ resp) => {
        /** @type {ServiceLocation[]} */
        return resp.locations.map((l) =>
          buildServiceLocation(l, culture, miles),
        );
      }),
  );
}

/**
 * @param {ServiceLocationResponse} location
 * @param {string} culture
 * @param {string} miles the translated value for the word 'miles'
 */
function buildServiceLocation(location, culture, miles) {
  const addressHTML = `
    ${location.street} <br />
    ${location.city}, ${location.stateCode} <br />
    ${location.postalCode}
  `;
  /** @type {ServiceLocation} */
  return {
    ...location,
    distance: `${formatNumber(location.distance, culture)} ${miles}`,
    addressHTML,
  };
}

/**
 * @typedef MapState
 * @property {google.maps.Map=} map
 * @property {google.maps.Marker[]} markers
 */

/**
 * @typedef ServiceLocationResponse
 * @property {string} id
 * @property {string} title
 * @property {string} street
 * @property {string} city
 * @property {string} stateCode
 * @property {string} postalCode
 * @property {string} phone
 * @property {number} coordLat
 * @property {number} coordLong
 * @property {number} distance
 */

/**
 * @typedef ServiceLocation
 * @property {string} id
 * @property {string} title
 * @property {string} street
 * @property {string} city
 * @property {string} stateCode
 * @property {string} postalCode
 * @property {string} phone
 * @property {number} coordLat
 * @property {number} coordLong
 * @property {string} distance
 * @property {string} addressHTML
 */
